import { AlertCircle, Check, CheckCircle2, Loader2, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

export type UploadItemStatus = 'pending' | 'uploading' | 'confirming' | 'done' | 'error';

export interface UploadItem {
  promptId: string;
  label: string;
  status: UploadItemStatus;
}

interface UploadProgressProps {
  items: UploadItem[];
  currentIndex: number;
  uploadPercent: number;
  phase: 'uploading' | 'confirming' | 'submitting' | 'done' | 'error';
  error: string | null;
  onRetry: () => void;
  onCancel: () => void;
  onDone: () => void;
}

function StatusIcon({ status }: { status: UploadItemStatus }) {
  switch (status) {
    case 'done':
      return <Check size={16} className='text-green-600' />;
    case 'error':
      return <AlertCircle size={16} className='text-red-500' />;
    case 'uploading':
    case 'confirming':
      return <Loader2 size={16} className='animate-spin text-primary' />;
    case 'pending':
      return <div className='h-4 w-4 rounded-full border-2 border-gray-300' />;
  }
}

export function UploadProgress({
  items,
  currentIndex,
  uploadPercent,
  phase,
  error,
  onRetry,
  onCancel,
  onDone,
}: UploadProgressProps) {
  const { t } = useTranslation();

  if (phase === 'done') {
    return (
      <div className='flex flex-col items-center gap-6 py-10 text-center'>
        <CheckCircle2 size={64} className='text-green-500' />
        <div>
          <h2 className='text-xl font-semibold text-gray-900'>{t('video.upload.success.title')}</h2>
          <p className='mt-2 text-sm text-gray-600'>{t('video.upload.success.message')}</p>
        </div>
        <button
          type='button'
          onClick={onDone}
          className='rounded-lg bg-primary px-6 py-3 font-medium text-white transition hover:bg-primary-dark'>
          {t('video.upload.success.backToDashboard')}
        </button>
      </div>
    );
  }

  const completedCount = items.filter((item) => item.status === 'done').length;
  const overallPercent =
    items.length > 0 ? Math.round(((completedCount + uploadPercent / 100) / items.length) * 100) : 0;

  const phaseLabel =
    phase === 'uploading'
      ? t('video.upload.uploadingVideo', { current: currentIndex + 1, total: items.length })
      : phase === 'confirming'
        ? t('video.upload.confirming')
        : phase === 'submitting'
          ? t('video.upload.submitting')
          : '';

  return (
    <div className='flex flex-col items-center gap-6 py-8'>
      {/* Header */}
      <div className='flex flex-col items-center gap-2 text-center'>
        <Upload size={32} className='text-primary' />
        <h2 className='text-lg font-semibold text-gray-900'>{t('video.upload.title')}</h2>
        <p className='text-sm text-gray-500'>{t('video.upload.subtitle')}</p>
      </div>

      {/* Per-video list */}
      <div className='w-full max-w-md space-y-3'>
        {items.map((item) => (
          <div
            key={item.promptId}
            className={cn(
              'flex items-center gap-3 rounded-lg border px-4 py-3',
              item.status === 'error' && 'border-red-200 bg-red-50',
              item.status === 'done' && 'border-green-200 bg-green-50',
              (item.status === 'uploading' || item.status === 'confirming') && 'border-primary/30 bg-primary/5',
              item.status === 'pending' && 'border-gray-200 bg-gray-50',
            )}>
            <StatusIcon status={item.status} />
            <span className='flex-1 text-sm font-medium text-gray-700'>{item.label}</span>
            {(item.status === 'uploading' || item.status === 'confirming') && (
              <span className='text-xs text-gray-500'>{t('video.upload.percentage', { percent: uploadPercent })}</span>
            )}
          </div>
        ))}
      </div>

      {/* Overall progress bar */}
      <div className='w-full max-w-md'>
        <div className='w-full overflow-hidden rounded-full bg-gray-200 h-2.5'>
          {phase === 'submitting' ? (
            <div className='h-2.5 w-1/3 animate-[indeterminate_1.5s_ease-in-out_infinite] rounded-full bg-primary' />
          ) : (
            <div
              className='h-2.5 rounded-full bg-primary transition-all duration-300'
              style={{ width: `${overallPercent}%` }}
            />
          )}
        </div>
        <p className='mt-2 text-center text-xs text-gray-500'>
          {phase === 'submitting'
            ? phaseLabel
            : phaseLabel || t('video.upload.percentage', { percent: overallPercent })}
        </p>
      </div>

      {/* Error state */}
      {phase === 'error' && error && (
        <div className='w-full max-w-md'>
          <div className='rounded-lg border border-red-200 bg-red-50 p-4 text-center'>
            <p className='text-sm text-red-700'>{error}</p>
          </div>
          <div className='mt-4 flex gap-3'>
            <button
              type='button'
              onClick={onRetry}
              className='flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary py-3 font-medium text-white transition hover:bg-primary-dark'>
              {t('video.upload.retry')}
            </button>
            <button
              type='button'
              onClick={onCancel}
              className='flex flex-1 items-center justify-center gap-2 rounded-lg border border-gray-300 py-3 font-medium text-gray-700 transition hover:bg-gray-50'>
              {t('video.upload.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
